import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, ListItemText, MenuItem, TextField, Typography, } from "@mui/material";
import { useAuth } from "@ssf/auth-components";
import { queryClient } from "@ssf/components";
import { MemdbBoolean, SpecialType } from "@ssf/memdb-api";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { parsePath, useLocation, useNavigate } from "react-router";
import { z } from "zod";
import { getIssues, participantsFromTournamentsQuery, QUERY_PARAMS, useCreateContender, useSchoolName, } from "../../lib/index.js";
import { ClubTeamStatusButton } from "../status/ClubTeamStatusButton.js";
import { StatusListItem } from "../status/StatusListItem.js";
const stateSchema = z.object({
    clubId: z.coerce.number().optional(),
});
export const RegTeamItem = ({ club, contenders, groups, isRegable, teamLink, isClassTournament, tournamentIds, currentMemberId, }) => {
    const { t } = useTranslation("RegTeamItem");
    const { user, isFedAdmin } = useAuth();
    const navigate = useNavigate();
    const { data: school } = useSchoolName(club.schoolid, {
        enabled: Boolean(club.schoolid),
    });
    const { state } = useLocation();
    const stateClubId = useMemo(() => {
        const parsed = stateSchema.safeParse(state);
        if (parsed.success) {
            return parsed.data.clubId;
        }
    }, [state]);
    const contender = contenders[club.id];
    const teamId = contender?.team.id.toString();
    const tournamentId = contender?.tournamentId.toString();
    const [isOpen, setOpen] = useState(!contender && stateClubId === club.id);
    const { mutate: createContender, isPending } = useCreateContender({
        onSuccess({ result }, { tournamentid }) {
            queryClient.invalidateQueries(participantsFromTournamentsQuery(tournamentIds));
            const destination = parsePath(teamLink({
                tournamentId: String(tournamentid),
                teamId: result.toString(),
            }));
            const search = new URLSearchParams(destination.search);
            search.set(QUERY_PARAMS.selectPlayers.name, QUERY_PARAMS.selectPlayers.value);
            navigate({ ...destination, search: `?${search}` });
        },
    });
    return (_jsxs(_Fragment, { children: [_jsxs(StatusListItem, { dense: true, label: club.name, to: teamId && tournamentId
                    ? teamLink({ tournamentId, teamId })
                    : undefined, onClick: () => setOpen(true), status: _jsx(ClubTeamStatusButton, { club: club, currentMemberId: currentMemberId, registration: contender && teamId && tournamentId
                        ? {
                            issues: contender.issues,
                            href: teamLink({ tournamentId, teamId }),
                        }
                        : undefined }), children: [_jsxs(ListItemText, { children: [club.name, contender?.team.name && contender.team.name !== club.name ? (_jsxs(Box, { sx: { display: "inline-block", typography: "caption", ml: 0.5 }, children: ["(", contender.team.name, ")"] })) : ("")] }), teamId && (_jsx(Typography, { variant: "body2", children: t(getIssues({
                            clubStatus: club.approvalStatus,
                            contenderIssues: contender?.issues,
                            isClubApproved: club.specialType !== SpecialType.Temp,
                        }).every(({ approved }) => approved)
                            ? "registered"
                            : "registered_init") }))] }), _jsxs(Dialog, { open: isOpen, onClose: () => setOpen(false), children: [_jsx(DialogTitle, { children: t("dialog.title", { name: club.name }) }), isRegable ? (_jsxs("form", { onSubmit: (event) => {
                            event.preventDefault();
                            const data = Object.fromEntries(new FormData(event.currentTarget));
                            const groupId = Number(data.groupId);
                            const classListRequired = groups.find(({ id }) => id === groupId)?.classListRequired;
                            createContender({
                                team: {
                                    name: data.school && data.className
                                        ? `${data.school} ${data.className}`
                                        : club.name,
                                    clubid: club.id,
                                    schoolid: club.schoolid,
                                    classsize: classListRequired ? -1 : 0,
                                },
                                captain: true,
                                leaderMemberId: isFedAdmin && currentMemberId !== Number(user?.profile.sub)
                                    ? currentMemberId
                                    : undefined,
                                groupId,
                                tournamentid: groups.find(({ id }) => id === groupId)
                                    ?.tournamentId,
                                updateClubReason: {
                                    id: club.id,
                                    ...(isClassTournament
                                        ? { schack4anreason: true }
                                        : { yes2chessreason: true }),
                                },
                            });
                        }, children: [_jsx(DialogContent, { children: t("dialog.content", { name: club.name }) }), _jsxs(Box, { sx: { mx: 3 }, children: [_jsx(TextField, { name: "groupId", label: t("dialog.label"), select: true, required: true, fullWidth: true, defaultValue: groups.length === 1 ? groups[0].id : undefined, children: groups.map(({ id, name, status }) => (_jsxs(MenuItem, { value: id, children: [name, status !== MemdbBoolean.True && ` (${t("dialog.closed")})`] }, id))) }), isClassTournament && (_jsxs(_Fragment, { children: [_jsx(TextField, { name: "school", label: t("dialog.school"), defaultValue: school?.name, required: true, fullWidth: true, sx: { my: 2 }, children: groups.map(({ id, name }) => (_jsx(MenuItem, { value: id, children: name }, id))) }), _jsx(TextField, { name: "className", label: t("dialog.className"), placeholder: t("dialog.className_placeholder"), defaultValue: club.className, required: true, fullWidth: true, children: groups.map(({ id, name }) => (_jsx(MenuItem, { value: id, children: name }, id))) })] }))] }), _jsxs(DialogActions, { children: [_jsx(Button, { color: "inherit", onClick: () => setOpen(false), children: t("dialog.close") }), _jsx(LoadingButton, { type: "submit", loading: isPending, children: t("dialog.submit") })] })] })) : (_jsxs(_Fragment, { children: [_jsx(DialogContent, { children: t("dialog.unableToRegister") }), _jsx(DialogActions, { children: _jsx(Button, { color: "inherit", onClick: () => setOpen(false), children: t("dialog.close") }) })] }))] })] }));
};
//# sourceMappingURL=RegTeamItem.js.map